/* Demo Feed page — shows items with status "feed".
   Users can queue items or delete them. Groups by section when on "all" tab. */

import { useState, useMemo } from "react";
import { ContentTypeTabs } from "@/components/feed/ContentTypeTabs";
import { Tabs } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, X } from "lucide-react";
import type { DemoItem } from "./demoData";
import { getSectionForTag } from "./demoData";
import { DemoFeedItemRow } from "./DemoFeedItemRow";
import { DemoDailyDigest } from "./DemoDailyDigest";
import type { MockCitation } from "./DemoDailyDigest";


const HIGHLIGHT_CLASS = "ring-2 ring-primary/60 rounded-md transition-all duration-300";

interface DemoFeedPageProps {
  items: DemoItem[];
  onQueueItem: (id: string) => void;
  onDeleteItem: (id: string) => void;
  onAddDigestItem?: (citation: MockCitation) => void;
  highlightedFeature?: string;
}

export function DemoFeedPage({
  items,
  onQueueItem,
  onDeleteItem,
  onAddDigestItem,
  highlightedFeature,
}: DemoFeedPageProps) {
  const [activeTab, setActiveTab] = useState("all");
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const feedItems = items.filter((item) => item.status === "feed");

  const availableTags = useMemo(
    () => [...new Set(feedItems.flatMap((item) => item.tags))].sort(),
    [feedItems],
  );

  const digestQueuedIds = useMemo(
    () => new Set(items.filter((i) => i.id.startsWith("digest-")).map((i) => i.id.replace("digest-", ""))),
    [items],
  );

  const provocativeCount = useMemo(
    () => feedItems.filter((item) => item.provocative).length,
    [feedItems],
  );

  const filtered = useMemo(() => {
    let result = feedItems;
    if (activeTab === "provocative") {
      result = result.filter((item) => item.provocative);
    } else if (activeTab === "for_you") {
      result = result.filter((item) => item.forYou);
    } else if (activeTab !== "all") {
      result = result.filter((item) => item.contentType === activeTab);
    }
    if (filterTag) {
      result = result.filter((item) => item.tags.includes(filterTag));
    }
    return [...result].sort((a, b) =>
      sortOrder === "asc" ? a.addedAt - b.addedAt : b.addedAt - a.addedAt,
    );
  }, [feedItems, activeTab, filterTag, sortOrder]);

  const showSections = activeTab === "all" && !filterTag;
  const showDigest = activeTab === "all" || activeTab === "for_you";

  /** Items grouped by section order, each sorted by addedAt within the section. */
  const groupedItems = useMemo(() => {
    if (!showSections) return filtered;
    return [...filtered].sort((a, b) => {
      const aSection = availableTags.indexOf(a.tags[0]);
      const bSection = availableTags.indexOf(b.tags[0]);
      if (aSection !== bSection) return aSection - bSection;
      return sortOrder === "asc" ? a.addedAt - b.addedAt : b.addedAt - a.addedAt;
    });
  }, [filtered, showSections, sortOrder, availableTags]);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
      <div className="sticky top-0 z-20 bg-background">
        <div className="flex items-center gap-3 px-4 md:px-6 pt-4 pb-2">
          <h1 className="text-lg font-bold text-heading-foreground">Feed</h1>
          <Badge variant="secondary" className="text-xs">
            {feedItems.length}
          </Badge>
        </div>
        <div className={highlightedFeature === "content-filter" || highlightedFeature === "tag-filter" || highlightedFeature === "sort-order" ? HIGHLIGHT_CLASS : ""}>
          <ContentTypeTabs
            activeTab={activeTab}
            onActiveTabChange={setActiveTab}
            showForYou
            provocativeCount={provocativeCount}
            filterTag={filterTag}
            onFilterTagChange={setFilterTag}
            availableTags={availableTags}
            sortOrder={sortOrder}
            onSortOrderChange={setSortOrder}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeTab === "for_you" && (
          <p className="px-4 md:px-6 pt-2 text-xs text-muted-foreground">
            Based on your completion history
          </p>
        )}
        {showDigest && (
          <DemoDailyDigest
            highlightedFeature={highlightedFeature}
            onAddItem={onAddDigestItem}
            queuedIds={digestQueuedIds}
          />
        )}
        {groupedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <p className="text-sm">No items in this category</p>
          </div>
        ) : (
          groupedItems.map((item, i) => {
            const sectionName = showSections ? getSectionForTag(item.tags[0]) : null;
            const prevSection = i > 0 && showSections ? getSectionForTag(groupedItems[i - 1].tags[0]) : null;
            const showHeader = sectionName !== null && sectionName !== prevSection;

            const nextSection = i < groupedItems.length - 1 && showSections ? getSectionForTag(groupedItems[i + 1].tags[0]) : null;
            const isLastInSection = i === groupedItems.length - 1 || nextSection !== sectionName;

            return (
              <div key={item.id}>
                {showHeader && (
                  <SectionHeader name={sectionName} highlighted={highlightedFeature === "sections" && prevSection === null} />
                )}
                <DemoFeedItemRow
                  item={item}
                  actions={[
                    {
                      icon: PlusCircle,
                      onClick: () => onQueueItem(item.id),
                      label: "Add to queue",
                    },
                    {
                      icon: X,
                      onClick: () => onDeleteItem(item.id),
                      label: "Delete",
                    },
                  ]}
                  hideBorder={isLastInSection}
                />
              </div>
            );
          })
        )}
      </div>
    </Tabs>
  );
}

function SectionHeader({ name, highlighted }: { name: string; highlighted?: boolean }) {
  return (
    <div className={`sticky z-10 bg-background flex items-center gap-3 px-4 md:px-6 pt-5 pb-2 ${highlighted ? HIGHLIGHT_CLASS : ""}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-primary whitespace-nowrap">
        {name}
      </span>
      <div className="flex-1 border-t border-border" />
    </div>
  );
}
